import {
    mapReactFilterValueTypeToRequestFilterValueType,
    mapReactMUIToFilterOperationTypes
} from '../common/enums/RequestFilterEnums';
import React from "react";

/**
 * Debounces a function, delaying its execution until after a specified wait time has passed since the last time it was invoked.
 * @param func The function to debounce.
 * @param delay The number of milliseconds to delay.
 * @returns A new debounced function.
 */
export const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            func.apply(null, args);
        }, delay);
    };
};

/**
 * Converts filter values from the table into query parameters for an API request.
 * @param filters The filters object from the table.
 * @param decimalFields An array of field names that should be treated as decimal numbers.
 * @param customLogic A function for custom processing of specific filter keys.
 * @returns An object containing the query parameters.
 */
export const getQueryParams = (filters: any, decimalFields: string[] = [], customLogic?: (key: string, value: any, queryParams: any) => boolean) => {
    const queryParams: any = {};

    for (const key in filters) {
        const value = filters[key];

        if (customLogic && customLogic(key, value, queryParams)) {
            continue;
        }

        if (value === "false") {
            queryParams[key] = false;
        } else if (key === 'filterMerchantId') {
            if (value !== null && value !== undefined && value.toString().length > 0) {
                queryParams['filterSubMerchantId'] = Number(value);
            }
        } else if (value !== null && value !== undefined && value.toString().length > 0) {
            if (decimalFields.includes(key) && !isNaN(parseFloat(value))) {
                queryParams[key] = Number(parseFloat(value).toFixed(2));
            } else {
                queryParams[key] = value;
            }
        }
    }
    return queryParams;
};

/**
 * Converts Material-UI DataGrid's filter model into a request filter format for the API.
 * @param tableFilters The filter model from the DataGrid.
 * @param columns The column definitions for the grid.
 * @returns An array of filters in the API's expected format.
 */
export const convertTableFiltersToRequestFilter = (tableFilters: any, columns: any[]) => {
    const convertedFilters: any[] = [];

    if (!tableFilters?.items) {
        return convertedFilters;
    }

    tableFilters.items.forEach((item: any) => {
        const findColumn = columns.find((x: any) => x.field === item.field);

        convertedFilters.push({
            field: item.field,
            values: [item.value.toString()],
            valueType: mapReactFilterValueTypeToRequestFilterValueType(item.value, findColumn?.columnType),
            operationType: mapReactMUIToFilterOperationTypes(item.operator)
        });
    });

    return convertedFilters;
};

interface CreateFilterHandlerProps {
    setDynamicFilter: (filters: any[]) => void;
    setQueryOptions: (options: any) => void;
    previousQueryParams: React.MutableRefObject<any>;
    columns: any[];
    filterStorageKey: string;
    isValueValid: (key: string, value: any) => boolean;
    decimalFields?: string[];
    getQueryParamsCustomLogic?: (key: string, value: any, queryParams: any) => boolean;
}

/**
 * Creates a debounced filter change handler for the data grid.
 * This handler processes filters, updates state, and persists filters to local storage.
 * @param props The configuration and state setters for the filter handler.
 * @returns A debounced function to be used as the filter change handler.
 */
export const createFilterHandler = ({
    setDynamicFilter,
    setQueryOptions,
    previousQueryParams,
    columns,
    filterStorageKey,
    isValueValid,
    decimalFields: decimalFieldsConfig = [],
    getQueryParamsCustomLogic
}: CreateFilterHandlerProps) => {
    const handleFilterChange = (
        filters: any,
        tableFilters: any,
    ) => {
        if (filters.clearFilter) {
            setDynamicFilter([]);
            localStorage.removeItem(filterStorageKey);
            setQueryOptions({});
            previousQueryParams.current = {};
            return;
        }

        const queryParams = getQueryParams(filters, decimalFieldsConfig, getQueryParamsCustomLogic);

        const allValuesValid = Object.entries(queryParams).every(([key, value]) => isValueValid(key, value));

        const previousKeysLength = Object.keys(previousQueryParams.current).length;
        const currentKeysLength = Object.keys(queryParams).length;

        if (JSON.stringify(previousQueryParams.current) === JSON.stringify(queryParams)) {
            return;
        }

        if (allValuesValid || (previousKeysLength > currentKeysLength)) {
            const dynamicFilters = convertTableFiltersToRequestFilter(tableFilters, columns);
            setDynamicFilter(dynamicFilters);
            setQueryOptions(queryParams);
            localStorage.setItem(filterStorageKey, JSON.stringify(queryParams));
        }

        previousQueryParams.current = queryParams;
    };

    return debounce(handleFilterChange, 500);
};
